import { useState, useEffect, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Button, Box, TextField, Autocomplete } from '@mui/material';
import { advancedSearchTickets } from '../../../api/tickets';
import TicketsResults from './TicketsResults';
import { Search } from "./Search";
import { getFacilitiesAndUsersList } from '../../../redux/slices/tickets';
import { useDispatch } from '../../../redux/store';

export default function TicketSearch() {

  const dispatch = useDispatch();

  const { facilities } = useSelector((state) => state.tickets.data);
  const [searchParams, setSearchParams] = useSearchParams();
  const [base] = useState(() => searchParams.get('base'), [searchParams]);

  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState({ searchValue: '', selectedSearchTypes: [], page: 1, pageSize: 20 });
  const [searchResults, setSearchResults] = useState({});
  const [filters, setFilters] = useState({
    highImportance: false,
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
    hasAttachment: false,
    complete: false,
    facility: null
  })
  const [initialFilters, setInitialFilters] = useState({...filters});
  const [initialSearchData, setInitialSearchData] = useState({ ...searchData });

  const filtersChanged = useMemo(() => JSON.stringify(filters) !== JSON.stringify(initialFilters), [initialFilters, filters]);

  const isSearchDirty = useMemo(() => searchData.searchValue !== initialSearchData.searchValue ||
    JSON.stringify(searchData.selectedSearchTypes) !== JSON.stringify(initialSearchData.selectedSearchTypes) ||
    filtersChanged, [searchData, initialSearchData, filtersChanged]);

  const searchTypes = [ 
    {value: 'messageSearch', label: 'Messages'},
    {value: 'userSearch', label: 'Users'},
    {value: 'baseTicketSearch', label: 'Subject'}
  ];

  useEffect(() => {
    if(!facilities?.length) {
      dispatch(getFacilitiesAndUsersList());
    }
    const urlSearchTypes = [];
    const getSearchTypeFromUrl = (type) => {
      if (searchParams.get(type)?.toLowerCase() === "true"){
        urlSearchTypes.push(searchTypes.find(searchType => searchType.value === type))
      }
    };
    searchTypes.forEach(type => getSearchTypeFromUrl(type.value));
    const urlData = {
      selectedSearchTypes: urlSearchTypes,
      searchValue: searchParams.get('searchValue'),
      pageSize: searchData.pageSize
    };
    const page = parseInt(searchParams.get('page'), 10) || 1;
    setSearchData({...urlData, page});
    setInitialSearchData(urlData);
    if(!searchResults.searchResults?.length){
      handleSearch(urlData, page);
    }
  }, []);

  const handleSearch = async (data, page) => {
    setLoading(true);
    setSearchData({...data, page});
    const submitData = { searchValue: data.searchValue, page, pageSize: searchData.pageSize }
    data.selectedSearchTypes.forEach((type) => { submitData[type.value] = true; });
    setSearchParams({ ...submitData, base});
    advancedSearchTickets({...submitData, filters}).then((res) => {
      setSearchResults(res.data || {});
      setInitialSearchData({ ...data });
      setInitialFilters({...filters});
    }).catch(() => enqueueSnackbar("Failed to load messages", { variant: "error" }))
    .finally(() => setLoading(false));
  };

  const searchDataMap = useMemo(
    () => searchResults?.searchResults?.map((searchResult) => <TicketsResults
      searchResult={searchResult} search={searchData.searchValue} key={searchResult.id}
    />), [searchResults]);

  const Filters = <Box sx={{display: 'flex', flexWrap: 'wrap', mb: 1}}>
    <Button
      variant={filters.hasAttachment ? "contained" : "outlined"}
      sx={{borderRadius: '20px', mr: 1, boxShadow: 'none'}}
      onClick={() => setFilters({...filters, hasAttachment: !filters.hasAttachment})}
      disableElevation
    >Has Attachment</Button>
    <Button
      variant={filters.highImportance ? "contained" : "outlined"}
      sx={{borderRadius: '20px', mr: 1, boxShadow: 'none'}}
      onClick={() => setFilters({...filters, highImportance: !filters.highImportance})}
      disableElevation
    >High Priority</Button>
    <Button
      variant={filters.complete ? "contained" : "outlined"}
      sx={{borderRadius: '20px', mr: 1, boxShadow: 'none'}}
      onClick={() => setFilters({...filters, complete: !filters.complete})}
      disableElevation
    >complete</Button>
  </Box>

  const SearchChildren = <Autocomplete
    sx={{width: '150px', padding: 0}}
    options={facilities}
    size="small"
    getOptionLabel={(option) => option.facilityName}
    onChange={(e, value) => {
      setFilters({...filters, facility: value ? value.id : null});
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        variant="outlined"
        size="small"
        placeholder='Select Facility'
        autoComplete="off"
        sx={{
          height: '40px',
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#f2f5f7',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee4e9' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#dee4e9' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#dee4e9' }
          },
        }}
      />
    )}
  />

  const handlePaginationChange = (page) => {
    setSearchData(data => ({ ...data, page }));
    handleSearch(searchData, page);
  };

  return <Search
    backToPath="/dashboard/tickets?status=active"
    searchTypes={searchTypes}
    searchData={searchData}
    setSearchData={setSearchData}
    handleSearch={handleSearch}
    isSearchDisabled={
      (!searchData.searchValue && !filters.facility) ||
      !isSearchDirty ||
      (!searchData.selectedSearchTypes.length && !filters.facility)
    }
    filtersElements={Filters}
    loading={loading}
    handlePaginationChange={handlePaginationChange}
    searchDataMap={searchDataMap}
    searchResults={searchResults}
    filters={filters}
    setFilters={setFilters}
    searchChildren={SearchChildren}
  />
}
